import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw } from 'lucide-react';
import { useAppStore } from '../store';
import { T } from '../i18n';

export default function MeasureOverlay({ onComplete, onSkip }) {
  const { lang, photo, setMeasure, measure } = useAppStore();
  const t = T[lang] || T.fr;
  
  const [taps, setTaps] = useState([]);
  const [showInput, setShowInput] = useState(false);
  const [cmValue, setCmValue] = useState('');
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imgBoundsRef = useRef(null);

  useEffect(() => {
    if (!photo) return;
    const img = new Image();
    img.onload = () => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const scale = Math.min(rect.width / img.width, rect.height / img.height);
      const iw = img.width * scale;
      const ih = img.height * scale;
      const ix = (rect.width - iw) / 2;
      const iy = (rect.height - ih) / 2;
      imgBoundsRef.current = { ix, iy, iw, ih, rw: rect.width, rh: rect.height };
    };
    img.src = photo;
  }, [photo]);
  
  // Draw taps and line on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw tap points
    taps.forEach((tap, i) => {
      // Outer glow
      ctx.beginPath();
      ctx.arc(tap.x, tap.y, 12, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(220, 60, 60, 0.3)';
      ctx.fill();
      
      // Inner circle
      ctx.beginPath();
      ctx.arc(tap.x, tap.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = '#DC3C3C';
      ctx.fill();
      
      // Number
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px Inter';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((i + 1).toString(), tap.x, tap.y);
    });
    
    // Draw line between points
    if (taps.length === 2) {
      ctx.beginPath();
      ctx.moveTo(taps[0].x, taps[0].y);
      ctx.lineTo(taps[1].x, taps[1].y);
      ctx.strokeStyle = '#DC3C3C';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [taps]);
  
  const handleTap = (e) => {
    if (taps.length >= 2) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Ensure tap is within the actual photo area (not letterbox margins)
    const b = imgBoundsRef.current;
    if (b && (x < b.ix || x > b.ix + b.iw || y < b.iy || y > b.iy + b.ih)) {
      return;
    }
    
    const newTaps = [...taps, { x, y }];
    setTaps(newTaps);
    
    if (newTaps.length === 2) {
      // Calculate pixel distance
      const dx = newTaps[1].x - newTaps[0].x;
      const dy = newTaps[1].y - newTaps[0].y;
      const pixDist = Math.sqrt(dx * dx + dy * dy);
      setMeasure({ taps: newTaps, pixDist });
      setShowInput(true);
    }
  };
  
  const handleConfirm = () => {
    const cm = parseFloat(cmValue);
    if (!cm || cm <= 0) return;
    
    const scale = measure.pixDist / cm; // pixels per cm
    setMeasure({
      ...measure,
      cmVal: cm,
      scale,
      hasMeasure: true,
      active: false
    });
    onComplete(cm, scale);
  };
  
  const handleReset = () => {
    setTaps([]);
    setShowInput(false);
    setCmValue('');
    setMeasure({ taps: [], pixDist: 0, cmVal: 0, scale: 0, hasMeasure: false, active: true });
  };
  
  const handleSkip = () => {
    setMeasure({ taps: [], pixDist: 0, cmVal: 0, scale: 0, hasMeasure: false, active: false });
    onSkip();
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-30"
    >
      {/* Instruction banner */}
      <div className="absolute top-0 left-0 right-0 bg-secondary text-white text-center py-3 px-4 z-40">
        <p className="font-bold text-sm">
          {taps.length === 0 && t.tap1}
          {taps.length === 1 && t.tap2}
          {taps.length === 2 && t.enter_cm}
        </p>
      </div>
      
      {/* Photo background with tap area */}
      <div 
        ref={containerRef}
        className="absolute inset-0 cursor-crosshair"
        onClick={handleTap}
        style={{
          backgroundImage: photo ? `url(${photo})` : 'none',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1E3A52'
        }}
      >
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      </div>
      
      {/* Input popup */}
      <AnimatePresence>
        {showInput && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="absolute bottom-24 left-4 right-4 z-50"
          >
            <div className="bg-background-secondary border border-white/10 rounded-2xl p-4 backdrop-blur-lg">
              <p className="text-secondary font-bold text-sm mb-3 text-center">
                {t.enter_cm}
              </p>
              
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="ex: 250"
                  value={cmValue}
                  onChange={(e) => setCmValue(e.target.value)}
                  className="flex-1 bg-background border border-secondary rounded-lg px-4 py-3 text-white text-lg font-bold text-center focus:outline-none focus:ring-2 focus:ring-secondary"
                  autoFocus
                  data-testid="measure-input"
                />
                <span className="text-secondary font-bold text-lg">cm</span>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleConfirm}
                  disabled={!cmValue || parseFloat(cmValue) <= 0}
                  className="flex-1 bg-secondary text-white font-bold py-3 rounded-xl disabled:opacity-50"
                  data-testid="confirm-measure"
                >
                  {t.confirm}
                </button>
                <button
                  onClick={handleReset}
                  className="px-4 py-3 bg-white/10 rounded-xl"
                  data-testid="reset-measure"
                >
                  <RotateCcw size={20} className="text-muted-foreground" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Skip button */}
      <button
        onClick={handleSkip}
        className="absolute bottom-4 left-4 right-4 py-3 bg-white/10 border border-white/20 rounded-xl text-muted-foreground font-medium z-40"
        data-testid="skip-measure"
      >
        {t.skip} - {t.no_measure_warning.split('—')[0]}
      </button>
      
      {/* Hint overlay */}
      {taps.length < 2 && !showInput && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div className="bg-black/70 backdrop-blur-sm rounded-2xl px-6 py-4 text-center">
            <p className="text-white font-bold text-sm">{t.measure_hint}</p>
            <p className="text-muted-foreground text-xs mt-2">
              {taps.length === 0 ? 'Tap 1' : 'Tap 2'}
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}
